import { Empty, Flex, notification } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getOrderClosedCheckoutListThunk,
    getOrderOpenedCheckoutListThunk,
    postOrderCloseThunk,
    postOrderSaveThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { UserRole, WorkStatus } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import FlexContainer from "@/components/FlexContainer";
import Header from "@/components/Header";
import Meta from "@/components/Meta";
import { HeaderReloadBtn } from "@/components/UI/HeaderReloadBtn";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";
import { useGlobalCooldown } from "@/hooks/useGlobalCooldown";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import {
    CheckoutQueueBlockedInfo,
    CheckoutQueueSyncingInfo,
} from "@/pages/Checkout/components/CheckoutQueueInfo";
import { removeIgnoreRemainingOrders } from "@/store/slices/offline/offline.slice";
import { setCurrentUser } from "@/store/slices/sv";
import { setCheckoutActiveCollapse } from "@/store/slices/ui";
import {
    clearCheckoutItem,
    clearMainCheckout,
} from "@/store/slices/work/work.slice";

import { CheckoutList } from "./components/CheckoutList";
import { ConfirmOrderModal } from "./modals/ConfirmOrderModal";
import { StyledCheckoutMainContainer } from "./Styled";
import {
    convertCheckoutListToMarketProducts,
    processPaymentForm,
} from "./utils";

function Checkout() {
    const { agentId } = useParams();

    const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
    const [chosenOrders, setChosenOrders] = useState<Record<string, boolean>>(
        {},
    );

    const dispatch = useAppDispatch();
    const { checkoutList } = useAppSelector((state) => state.work);
    const { id, role } = useAppSelector((state) => state.user);
    const { team } = useAppSelector((state) => state.sv);
    const { checkoutDetails } = useAppSelector((state) => state.progress);
    const { workStatus } = useAppSelector((state) => state.work);
    const { offlineMode, ignoreRemainingsOrders, requestQueue } =
        useAppSelector((state) => state.offline);

    const { disabled } = useGlobalCooldown(30_000);

    const { isRunning: isRunningQueue } = useQueueInfo();

    const isAnyOrderChosen = useMemo(
        () => Object.values(chosenOrders).some(Boolean),
        [chosenOrders],
    );

    const closeConfirmModal = () => setIsConfirmModalOpened(false);

    const [handleResetCheckouts, resetCheckoutsLoading] = useAsyncAction(
        async () => {
            setChosenOrders({});
            try {
                await Promise.all([
                    dispatch(
                        getOrderOpenedCheckoutListThunk({
                            agentId: agentId || id,
                        }),
                    ),
                    dispatch(
                        getOrderClosedCheckoutListThunk({
                            agentId: agentId || id,
                            targetDate: dayjs().format(backendDateFormat.date),
                        }),
                    ),
                ]);
            } catch {
                notification.error({ message: "Помилка при оновленні кошика" });
            }
        },
    );

    const [handleCloseOrder, isProcessingClosingOrder] = useAsyncAction(
        async () => {
            const filtered = Object.fromEntries(
                Object.entries(checkoutList).filter(
                    ([orderId, order]) =>
                        chosenOrders[orderId] === true &&
                        order.orderType !== "p" &&
                        order.orderType !== "r",
                ),
            );

            let response;
            if (Object.keys(filtered).length) {
                const request = convertCheckoutListToMarketProducts(
                    filtered,
                    ignoreRemainingsOrders,
                );

                response = await dispatch(postOrderSaveThunk(request));
            }

            if (!response?.error || Object.keys(filtered).length === 0) {
                const trueIds = Object.keys(chosenOrders).filter(
                    (id) => chosenOrders[id],
                );
                dispatch(clearCheckoutItem(trueIds));

                const details = trueIds
                    .map((orderId) => {
                        const paymentForm = checkoutList[orderId]?.paymentForm;
                        const {
                            isCarrier,
                            comment,
                            commentChecked,
                            isPayOnDelivery,
                            isSelfPickup,
                        } = checkoutDetails[orderId] ?? {};

                        return processPaymentForm(
                            {
                                commentDetails: {
                                    checked: commentChecked,
                                    comment,
                                },
                                isCarrier: { checked: isCarrier },
                                isSelfDelivery: { checked: isSelfPickup },
                                isPayOnDelivery: { checked: isPayOnDelivery },
                            },
                            orderId,
                            paymentForm,
                            checkoutList[orderId].customShipmentDate ===
                                checkoutList[orderId].shipmentDate
                                ? ""
                                : checkoutList[orderId].customShipmentDate,
                        );
                    })
                    .flat();

                try {
                    await dispatch(
                        postOrderCloseThunk({
                            agentId: isManagerRole(role) ? agentId : id,
                            orderIds: trueIds,
                            details,
                        }),
                    ).unwrap();

                    notification.success({ message: "Замовлення оформлено" });

                    dispatch(removeIgnoreRemainingOrders(trueIds));
                    dispatch(setCheckoutActiveCollapse(null));

                    await handleResetCheckouts();
                } catch (err) {
                    notification.error({
                        message: err instanceof Error ? err.message : "Помилка",
                    });
                }

                closeConfirmModal();
            }
        },
    );

    const orderSaveQueue = requestQueue?.filter((item) => {
        if (item.status === QueueTaskStatus.Error) return false;

        const checkoutCommands = [
            "postOrderSave",
            "postOrderSaveOne",
            "updateCheckout",
        ];

        return item.commands.some((cmd) => checkoutCommands.includes(cmd.type));
    });

    const uniqueOrdersCount = useMemo(() => {
        if (!orderSaveQueue?.length) return 0;

        const uniqueIds = new Set(
            orderSaveQueue
                .map((item) => item?.metadata?.orderId)
                .filter(Boolean),
        );

        return uniqueIds.size;
    }, [orderSaveQueue]);

    useEffect(() => {
        if (role === "sv" && !offlineMode) {
            dispatch(setCurrentUser(agentId));
            dispatch(clearMainCheckout());
            handleResetCheckouts();
        }
    }, []);

    const hasPendingQueue = orderSaveQueue.length > 0;
    const isQueueBlocked = !isRunningQueue && hasPendingQueue && !offlineMode;
    const isQueueSyncing = isRunningQueue && hasPendingQueue;
    const isCheckoutAvailable = !hasPendingQueue || offlineMode;

    const renderQueueStatus = useCallback(() => {
        if (isQueueBlocked) return <CheckoutQueueBlockedInfo />;
        if (isQueueSyncing) {
            return <CheckoutQueueSyncingInfo count={uniqueOrdersCount} />;
        }

        return null;
    }, [isQueueBlocked, isQueueSyncing, uniqueOrdersCount]);

    const renderCheckout = useCallback(() => {
        if (!isCheckoutAvailable) return null;

        return (
            <>
                <CheckoutList
                    chosenOrders={chosenOrders}
                    setChosenOrders={setChosenOrders}
                    resetCheckouts={handleResetCheckouts}
                />

                {!offlineMode && isAnyOrderChosen && (
                    <FlexContainer>
                        <BaseButton
                            onClick={() => setIsConfirmModalOpened(true)}
                            type="primary"
                            disabled={!isAnyOrderChosen}
                        >
                            Оформити замовлення
                        </BaseButton>
                    </FlexContainer>
                )}
            </>
        );
    }, [
        chosenOrders,
        handleResetCheckouts,
        isAnyOrderChosen,
        isCheckoutAvailable,
        offlineMode,
    ]);

    return (
        <>
            <Meta title="Checkout" />
            <Header
                path={role === UserRole.Sv && !offlineMode && "/sv-checkout"}
                titleLoading={resetCheckoutsLoading}
                title={
                    role === UserRole.Sv
                        ? team?.members?.find((item) => item.userId === agentId)
                              .fullName
                        : undefined
                }
                rightActions={
                    <HeaderReloadBtn
                        onClick={handleResetCheckouts}
                        disabled={disabled || offlineMode}
                    />
                }
            />

            <StyledCheckoutMainContainer>
                {workStatus === WorkStatus.StartWork || role === UserRole.Sv ? (
                    <>
                        {renderQueueStatus()}

                        {renderCheckout()}
                    </>
                ) : (
                    <Flex justify="center" align="center">
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Робочий день не розпочато"
                        />
                    </Flex>
                )}
            </StyledCheckoutMainContainer>

            <ConfirmOrderModal
                isModalOpened={isConfirmModalOpened}
                isProcessing={isProcessingClosingOrder}
                isChosenOrder={isAnyOrderChosen}
                handleCancel={closeConfirmModal}
                handleCloseOrder={handleCloseOrder}
            />
        </>
    );
}

export default Checkout;
